// MFA hooks (ART-317) — the WRAPPER layer. Person-scoped (`/auth/mfa…`), so they live under ['me', 'mfa'] like the
// other personal surfaces, untouched by band switches and band-scoped realtime invalidation.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

const KEY = ["me", "mfa"] as const;

/** `GET /auth/mfa` — whether MFA is on, and how many devices currently skip the code. */
export function useMfaStatus() {
  const { api } = useApp();
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/auth/mfa", {});
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/** `POST /auth/mfa/enable/start` — email the caller a code; resolves to the challenge to confirm with. */
export function useStartEnableMfa() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/auth/mfa/enable/start", {});
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/** `POST /auth/mfa/enable` — the emailed code in, MFA on. 401 = wrong or expired code. */
export function useEnableMfa() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { challengeToken: string; code: string }) => {
      const { error, response } = await api.POST("/api/v1/auth/mfa/enable", { body: input });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** `POST /auth/mfa/disable` — the current password in, MFA off and trusted devices forgotten. 401 = wrong password. */
export function useDisableMfa() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password: string) => {
      const { error, response } = await api.POST("/api/v1/auth/mfa/disable", { body: { password } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** `DELETE /auth/trusted-devices` — every trusted device asks for the code again. */
export function useForgetTrustedDevices() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/api/v1/auth/trusted-devices", {});
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
